import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { LogOut, Building2, Settings, HelpCircle, Eye, EyeOff, PlayCircle } from "lucide-react";
import StudioBrandingDialog from "./StudioBrandingDialog";
import SettingsDialog from "./SettingsDialog";
import WelcomeDialog from "./WelcomeDialog";
import TutorialVideo from "./TutorialVideo";

export default function TopBar() {
  const { user, logout, setUser, previewClient, setPreviewClient } = useAuth();
  const navigate = useNavigate();
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const isDesigner = user?.role === "designer";
  const isAdmin = user?.role === "admin";
  const isOwner = isDesigner && !user?.workspace_owner_id;
  const isNewOwner = isOwner && !user?.onboarded;

  // Auto-open the welcome guide once for brand-new owners
  useEffect(() => {
    if (isNewOwner) setTipsOpen(true);
  }, [isNewOwner]);

  const closeTips = async (o) => {
    if (o) { setTipsOpen(true); return; }
    setTipsOpen(false);
    if (isNewOwner) {
      try { const { data } = await api.post("/me/onboarded"); setUser(data); }
      catch { setUser({ ...user, onboarded: true }); }
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#EBE5E0]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <button data-testid="brand-home-btn" onClick={() => navigate("/")} className="flex items-baseline gap-2">
          <span className="font-serif text-2xl text-[#2B2622] tracking-wide">WorkRoom</span>
        </button>
        <div className="flex items-center gap-4">
          {isDesigner && (
            <button data-testid="preview-client-btn" onClick={() => setPreviewClient(!previewClient)}
              className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider transition-colors ${previewClient ? "text-[#A68A6B]" : "text-[#5C5651] hover:text-[#A68A6B]"}`}
              title={previewClient ? "Exit client preview" : "Preview as client"}>
              {previewClient ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
              <span className="hidden sm:inline">{previewClient ? "Exit preview" : "Preview as client"}</span>
            </button>
          )}
          {isDesigner && !previewClient && (
            <button data-testid="open-tutorial-btn" onClick={() => setTutorialOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#5C5651] hover:text-[#A68A6B] transition-colors"
              title="Watch the studio tutorial">
              <PlayCircle size={16} strokeWidth={1.5} />
              <span className="hidden sm:inline">Tutorial</span>
            </button>
          )}
          {isDesigner && !previewClient && (
            <button data-testid="open-tips-btn" onClick={() => setTipsOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#5C5651] hover:text-[#A68A6B] transition-colors"
              title="Helpful tips">
              <HelpCircle size={16} strokeWidth={1.5} />
              <span className="hidden sm:inline">Tips</span>
            </button>
          )}
          {(isDesigner || isAdmin) && !previewClient && (
            <button data-testid="open-settings-btn" onClick={() => setSettingsOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider italic text-[#5C5651] hover:text-[#A68A6B] transition-colors"
              title="Project settings">
              <Settings size={16} strokeWidth={1.5} />
              <span className="hidden sm:inline">Settings</span>
            </button>
          )}
          {isDesigner && !previewClient && (
            <button data-testid="open-branding-btn" onClick={() => setBrandingOpen(true)}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#5C5651] hover:text-[#A68A6B] transition-colors"
              title="Studio branding">
              <Building2 size={16} strokeWidth={1.5} />
              <span className="hidden sm:inline">Branding</span>
            </button>
          )}
          <div className="text-right hidden sm:block">
            <p data-testid="current-user-name" className="text-sm text-[#2B2622] font-medium leading-tight">{user?.name}</p>
            <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{previewClient ? "client (preview)" : user?.role}</p>
          </div>
          <div className="w-9 h-9 bg-[#A68A6B] text-white flex items-center justify-center font-serif text-lg">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <button data-testid="logout-btn" onClick={logout}
            className="text-[#5C5651] hover:text-[#9E4747] transition-colors" title="Sign out">
            <LogOut size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {previewClient && (
        <div data-testid="preview-client-banner"
          className="bg-[#A68A6B] text-white text-center text-[11px] uppercase tracking-[0.18em] py-1.5">
          Previewing as your client — designer tools are hidden
          <button data-testid="preview-exit-banner-btn" onClick={() => setPreviewClient(false)}
            className="ml-3 underline normal-case tracking-normal hover:text-white/80">Exit</button>
        </div>
      )}
      {isDesigner && !previewClient && (
        <StudioBrandingDialog open={brandingOpen} onOpenChange={setBrandingOpen} />
      )}
      {(isDesigner || isAdmin) && !previewClient && (
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      )}
      {isDesigner && (
        <WelcomeDialog open={tipsOpen} onOpenChange={closeTips} />
      )}
      {isDesigner && !previewClient && (
        <TutorialVideo open={tutorialOpen} onOpenChange={setTutorialOpen} />
      )}
    </header>
  );
}
